import {
  AgentResultSet,
  AgentSessionDetail,
  AgentSessionSummary,
  AgentTurn,
  SearchResult,
} from '../../services/api';
import {
  getSearchResultMediaKey,
  mergeUniqueSearchResults,
} from '../../utils/searchResultIdentity';
import {
  AgenticAssistantMessageView,
  AgenticSessionDetailView,
  AgenticSessionSummaryView,
  AgenticThreadMessage,
} from './types';

const deriveTitleFromTurns = (turns: AgentTurn[]): string | null => {
  const firstMessage = turns.find(
    (turn) => typeof turn.user_message?.content === 'string' && turn.user_message.content.trim().length > 0,
  );
  if (!firstMessage) return null;
  const content = firstMessage.user_message.content.trim();
  if (content.length <= 100) return content;
  const clipped = content.slice(0, 100).trim();
  const lastSpaceIndex = clipped.lastIndexOf(' ');
  if (lastSpaceIndex >= 60) {
    return `${clipped.slice(0, lastSpaceIndex)}...`;
  }
  return `${clipped}...`;
};

const sortResultSets = (resultSets: AgentResultSet[]): AgentResultSet[] => (
  resultSets.map((resultSet, inputIndex) => ({ resultSet, inputIndex })).map(({ resultSet, inputIndex }) => {
    const dedupedResults = mergeUniqueSearchResults([], resultSet.results || []);
    return {
      resultSet: dedupedResults === resultSet.results && dedupedResults.length === resultSet.result_count
        ? resultSet
        : {
            ...resultSet,
            results: dedupedResults,
            result_count: dedupedResults.length,
          },
      inputIndex,
    };
  }).sort((left, right) => {
    const leftPosition = left.resultSet.position;
    const rightPosition = right.resultSet.position;
    if (leftPosition != null && rightPosition != null && leftPosition !== rightPosition) {
      return leftPosition - rightPosition;
    }
    if (leftPosition != null && rightPosition == null) return -1;
    if (leftPosition == null && rightPosition != null) return 1;
    // A legacy response has no persisted ordinal. Its array order is the
    // server's only ordering contract, so retain that order exactly.
    return left.inputIndex - right.inputIndex;
  }).map(({ resultSet }) => resultSet)
);

export const getDefaultResultSetId = (resultSets: AgentResultSet[]): string | null => {
  if (!resultSets.length) return null;
  const selection = resultSets.find((set) => set.origin === 'selection' && set.results.length > 0);
  if (selection) return selection.result_set_id;
  const firstWithResults = resultSets.find((set) => set.results.length > 0);
  if (firstWithResults) return firstWithResults.result_set_id;
  return resultSets[0].result_set_id;
};

const collectAllResults = (turns: AgentTurn[]): SearchResult[] => {
  let merged: SearchResult[] = [];
  turns.forEach((turn) => {
    turn.result_sets.forEach((resultSet) => {
      merged = mergeUniqueSearchResults(merged, resultSet.results || []);
    });
  });
  return merged;
};

const buildAssistantMessage = (
  turn: AgentTurn,
  selectedResultSetId: string | null,
): AgenticAssistantMessageView | null => {
  const assistantMessage = turn.assistant_message;
  if (!assistantMessage && turn.status !== 'failed' && turn.result_sets.length === 0) {
    return null;
  }

  const sortedResultSets = sortResultSets(turn.result_sets || []);
  const fallbackResultSetId = getDefaultResultSetId(sortedResultSets);

  return {
    id: assistantMessage?.id || `${turn.turn_id}:assistant`,
    role: 'assistant',
    turnId: turn.turn_id,
    content: assistantMessage?.content || '',
    timestamp: assistantMessage?.timestamp || turn.updated_at,
    toolTrace: turn.tool_trace || [],
    resultSets: sortedResultSets,
    activeResultSetId: selectedResultSetId ?? fallbackResultSetId,
    errorMessage: turn.status === 'failed' ? turn.error?.message || 'Turn failed' : null,
  };
};

export const mapSessionSummary = (
  summary: AgentSessionSummary,
  detail?: AgentSessionDetail | null,
): AgenticSessionSummaryView => ({
  ...summary,
  derived_title: summary.title || deriveTitleFromTurns(detail?.turns || []) || null,
});

export const mapSessionDetail = (
  detail: AgentSessionDetail,
  selectedResultSets: Record<string, string | null> = {},
): AgenticSessionDetailView => {
  const threadMessages: AgenticThreadMessage[] = [];

  detail.turns.forEach((turn) => {
    threadMessages.push({
      id: turn.user_message.id,
      role: 'user',
      turnId: turn.turn_id,
      content: turn.user_message.content,
      timestamp: turn.user_message.timestamp,
    });

    const assistantMessage = buildAssistantMessage(turn, selectedResultSets[turn.turn_id] ?? null);
    if (assistantMessage) {
      threadMessages.push(assistantMessage);
    }
  });

  return {
    ...detail,
    derived_title: detail.title || deriveTitleFromTurns(detail.turns) || null,
    all_results: collectAllResults(detail.turns),
    thread_messages: threadMessages,
    selected_result_sets: detail.turns.reduce<Record<string, string | null>>((acc, turn) => {
      acc[turn.turn_id] = selectedResultSets[turn.turn_id] ?? getDefaultResultSetId(turn.result_sets || []);
      return acc;
    }, {}),
  };
};

export const patchDetailResults = (
  detail: AgenticSessionDetailView,
  matcher: (result: SearchResult) => boolean,
  patch: Partial<SearchResult>,
): AgenticSessionDetailView => {
  let hasChanges = false;

  const nextTurns = detail.turns.map((turn) => {
    const nextResultSets = turn.result_sets.map((resultSet) => {
      let resultSetChanged = false;
      const nextResults = resultSet.results.map((result) => {
        if (!matcher(result)) return result;
        resultSetChanged = true;
        hasChanges = true;
        return { ...result, ...patch };
      });
      return resultSetChanged ? { ...resultSet, results: nextResults } : resultSet;
    });

    const turnChanged = nextResultSets.some((resultSet, index) => resultSet !== turn.result_sets[index]);
    return turnChanged ? { ...turn, result_sets: nextResultSets } : turn;
  });

  if (!hasChanges) return detail;

  return mapSessionDetail(
    { ...detail, turns: nextTurns },
    detail.selected_result_sets,
  );
};

export const getResultSetResults = (
  message: AgenticAssistantMessageView,
): SearchResult[] => {
  const activeResultSetId = message.activeResultSetId || getDefaultResultSetId(message.resultSets);
  if (!activeResultSetId) return [];
  return message.resultSets.find((resultSet) => resultSet.result_set_id === activeResultSetId)?.results || [];
};

export const collectThreadVisibleResults = (messages: AgenticThreadMessage[]): SearchResult[] => {
  const merged = new Map<string, SearchResult>();

  messages.forEach((message) => {
    if (message.role !== 'assistant') return;
    message.resultSets.forEach((resultSet) => {
      if (!resultSet.results.length) return;
      resultSet.results.forEach((result) => {
        merged.set(getSearchResultMediaKey(result), result);
      });
    });
  });

  return Array.from(merged.values());
};
